import { captureMessage, setTags } from "@sentry/react";
import { getAppSessionUuid } from "./config";
import { PAGE_URLS } from "./constants";
import { triggerGa } from "./gaUtils";
import {
  loadSessionState,
  removeSessionState,
  saveSessionState,
} from "./sessionStorage";

// Inside the native app, the loader coming back with an empty page can mean
// the document itself was replaced (a native recovery after the WebContent
// process died, the native source being re-applied, a pull-to-refresh) or only
// that React remounted a subtree. Each document records itself in
// sessionStorage — which WebKit keeps outside the WebContent process — so the
// next document in the same native app session can report how it replaced the
// previous one. A process that was killed never runs pagehide/unload, so the
// record is refreshed on a heartbeat rather than on exit.
//
// Cost: the Sentry error quota runs out early in each billing cycle with no
// pay-as-you-go budget, so every event here competes with real errors. Sentry
// only hears about replacements that involve manual sourcing (the CAT-496
// investigation), capped per app session. Every replacement is counted in GA
// instead, which costs nothing.

export const DOCUMENT_LIFECYCLE_SENTRY_SOURCE = "document-lifecycle";
const DOCUMENT_RECORD_KEY = "wl_document_lifecycle";
const RELOAD_REASON_KEY = "wl_document_reload_reason";
const HEARTBEAT_MS = 5000;
const MAX_SENTRY_REPORTS_PER_APP_SESSION = 5;
const SENTRY_REPORTED_PATHS = [
  PAGE_URLS.SOURCING_MANUAL,
  PAGE_URLS.BRAND_SOURCING_MANUAL,
];

const isSentryReportedPath = (path) =>
  !!path &&
  SENTRY_REPORTED_PATHS.some(
    (base) => path === base || path.startsWith(`${base}/`)
  );

// Held until App has mounted: Firebase Analytics is not initialised yet when
// the document starts.
let pendingGaReplacement = null;

const createDocumentId = () => {
  try {
    if (window.crypto?.randomUUID) return window.crypto.randomUUID();
  } catch (error) {
    //
  }
  return `${Date.now()}-${Math.random().toString(36).slice(2)}`;
};

const getNavigationType = () => {
  try {
    return performance.getEntriesByType("navigation")[0]?.type || "unknown";
  } catch (error) {
    return "unknown";
  }
};

/**
 * Tags the next document so it can tell an intentional in-app reload apart
 * from one it did not ask for. Call right before `window.location.reload()`.
 */
export const markDocumentReloadReason = (reason) => {
  saveSessionState(RELOAD_REASON_KEY, reason);
};

export const startDocumentLifecycleDiagnostics = () => {
  if (!window.ReactNativeWebView) return;

  const documentId = createDocumentId();
  const navigationType = getNavigationType();
  const appSessionUuid = getAppSessionUuid() || "";
  const startedAt = Date.now();
  const previous = loadSessionState(DOCUMENT_RECORD_KEY);
  const reloadReason = loadSessionState(RELOAD_REASON_KEY);
  removeSessionState(RELOAD_REASON_KEY);

  setTags({
    document_id: documentId,
    document_navigation_type: navigationType,
    app_session_uuid: appSessionUuid,
  });

  // Only a replacement within the same native app session is interesting; a
  // fresh app launch starts a new appSessionUUID.
  const isReplacement =
    !!previous?.documentId && previous.appSessionUuid === appSessionUuid;
  let sentryReports = isReplacement ? previous.sentryReports || 0 : 0;
  const currentPath = window.location.pathname;
  const msSincePreviousHeartbeat = isReplacement
    ? startedAt - previous.lastSeen
    : null;

  if (isReplacement) {
    pendingGaReplacement = {
      navigationType,
      reloadReason: reloadReason || "none",
      previousPath: previous.path,
      msSincePreviousHeartbeat,
    };
  }

  if (
    isReplacement &&
    sentryReports < MAX_SENTRY_REPORTS_PER_APP_SESSION &&
    (isSentryReportedPath(previous.path) || isSentryReportedPath(currentPath))
  ) {
    sentryReports += 1;
    captureMessage("Web document replaced within app session", {
      level: "info",
      tags: {
        source: DOCUMENT_LIFECYCLE_SENTRY_SOURCE,
        document_navigation_type: navigationType,
        document_reload_reason: reloadReason || "none",
        previous_document_hidden: String(!!previous.hidden),
      },
      extra: {
        previous_document_id: previous.documentId,
        previous_path: previous.path,
        current_path: currentPath,
        ms_since_previous_heartbeat: msSincePreviousHeartbeat,
        previous_document_age_ms: previous.lastSeen - previous.startedAt,
        sentry_reports_in_app_session: sentryReports,
      },
    });
  }

  // Pathname only: query strings can carry OAuth codes and tokens.
  const writeRecord = () => {
    saveSessionState(DOCUMENT_RECORD_KEY, {
      documentId,
      appSessionUuid,
      startedAt,
      lastSeen: Date.now(),
      path: window.location.pathname,
      hidden: document.visibilityState === "hidden",
      sentryReports,
    });
  };

  writeRecord();
  setInterval(writeRecord, HEARTBEAT_MS);
  document.addEventListener("visibilitychange", writeRecord);
};

/**
 * Counts the replacement recorded at startup in GA. Call once Firebase
 * Analytics is ready (App's mount effect); later calls are no-ops.
 */
export const reportDocumentLifecycleToGa = () => {
  if (!pendingGaReplacement) return;
  const replacement = pendingGaReplacement;
  pendingGaReplacement = null;
  try {
    triggerGa({
      category: "document_lifecycle",
      action: "web_document_replaced",
      label: replacement.navigationType,
      label2: replacement.reloadReason,
      label3: replacement.previousPath || "",
      eventValue: Math.max(0, replacement.msSincePreviousHeartbeat || 0),
    });
  } catch (error) {
    //
  }
};
